import { TargetRockResult } from '../../@types/gheResults/types';
import ChartResults from '../chart/chartResults';
import { LazyMount } from '../chart/LazyMount';

type SeriesPoint = {
  x: number;
  y: number;
};

type ChartSeries = {
  name: string;
  data: SeriesPoint[];
  color?: string;
  dashStyle?: string;
  lineWidth?: number;
};

type Props = {
  rockResult: TargetRockResult;
};

const normalizeLabel = (label: string): string => label.trim().toLowerCase();
const isLegacyMlAnalogueLabel = (label: string): boolean => {
  const normalized = normalizeLabel(label);
  return normalized === 'ml' || normalized === 'analogue';
};

const normalizeCurveLabel = (
  label: string,
  chartKind: 'krel' | 'pc'
): string => {
  if (!isLegacyMlAnalogueLabel(label)) {
    return label;
  }
  if (chartKind === 'pc') {
    return normalizeLabel(label) === 'ml' ? 'pc ML' : 'pc Analogue';
  }
  return label;
};

const resolveSeriesStyle = (label: string) => {
  const normalized = normalizeLabel(label);
  const isAnalogue = normalized.includes('analogue');
  const isImbibition = normalized.includes('imbibition');
  const isPc = normalized.startsWith('pc');
  const isKrco2 = normalized.includes('krco2');
  const isKrw = normalized.includes('krw');

  if (isPc) {
    return {
      color: isAnalogue ? '#111111' : '#69b3e7',
      dashStyle: 'Solid',
      lineWidth: 2
    };
  }

  if (isKrco2) {
    return {
      color: isAnalogue ? '#b7b7b7' : '#111111',
      dashStyle: isImbibition ? 'ShortDash' : 'Solid',
      lineWidth: 2
    };
  }

  if (isKrw) {
    return {
      color: isAnalogue ? '#69b3e7' : '#0d31d0',
      dashStyle: isImbibition ? 'ShortDash' : 'Solid',
      lineWidth: isImbibition ? 4 : 2
    };
  }

  return {
    color: isAnalogue ? '#fa8c16' : '#1677ff',
    dashStyle: isAnalogue ? 'ShortDash' : 'Solid',
    lineWidth: isAnalogue ? 2 : 3
  };
};

const mapSeries = (
  series: TargetRockResult['krel_curves'],
  chartKind: 'krel' | 'pc'
): ChartSeries[] =>
  series.map(curve => {
    const normalizedLabel = normalizeCurveLabel(curve.label, chartKind);
    const style = resolveSeriesStyle(normalizedLabel);
    return {
      name: normalizedLabel,
      ...style,
      data: curve.x_values.map((x, index) => ({
        x,
        y: curve.y_values[index]
      }))
    };
  });

export const GheRockResultsChart = ({ rockResult }: Props) => {
  return (
    <div
      style={{
        marginBottom: '1.25rem',
        padding: '0.5rem 0'
      }}
      data-testid={`ghe-rock-${rockResult.rock_id}`}
    >
      <LazyMount style={{ minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height="320px"
          title={`${rockResult.rock_id} - Relative Permeability`}
          xAxis="Water Saturation"
          xAxisMin={0}
          xAxisMax={1}
          yAxis="Relative Permeability"
          data={mapSeries(rockResult.krel_curves, 'krel')}
        />
      </LazyMount>
      <LazyMount style={{ minHeight: 360 }}>
        <ChartResults
          containerWidth="100%"
          height="320px"
          title={`${rockResult.rock_id} - Capillary Pressure`}
          xAxis="Water Saturation"
          xAxisMin={0}
          xAxisMax={1}
          yAxis="Capillary Pressure"
          yAxisType="logarithmic"
          data={mapSeries(rockResult.pc_curves, 'pc')}
        />
      </LazyMount>
    </div>
  );
};
